import config from '../../../config';

/**
 * Build Langfuse trace URL
 * @param {string} smartContextTraceId - The trace ID from Langfuse
 * @returns {string} The Langfuse trace URL or empty string if invalid
 */
export const buildLangfuseUrl = (smartContextTraceId) => {
  if (!smartContextTraceId || !config.langfuse?.baseUrl || !config.langfuse?.autoAcProjectId) {
    return '';
  }
  return `${config.langfuse.baseUrl}/project/${config.langfuse.autoAcProjectId}/traces/${smartContextTraceId}`;
};

/**
 * Tokenize a string into words AND whitespace.
 *
 * We keep whitespace as tokens so spacing and punctuation
 * are preserved exactly when rendering the diff.
 *
 * Example:
 * "Hi there!" → ["Hi", " ", "there!"]
 */
export const tokenize = (text) => text.split(/(\s+)/).filter(Boolean);

/**
 * Builds a Longest Common Subsequence (LCS) matrix.
 *
 * The LCS matrix is a 2D table where:
 * - Rows represent prefixes of the original token array
 * - Columns represent prefixes of the modified token array
 *
 * dp[i][j] contains the length of the longest sequence of tokens
 * that appear in the same order in:
 *   - original[0..i-1]
 *   - modified[0..j-1]
 *
 * This matrix does NOT store the diff itself — only the length
 * of the LCS. We later backtrack through it to recover the diff.
 */
export const buildLCSMatrix = (original, modified) => {
  const dp = Array(original.length + 1)
    .fill(null)
    .map(() => Array(modified.length + 1).fill(0));

  for (let i = 1; i <= original.length; i++) {
    for (let j = 1; j <= modified.length; j++) {
      dp[i][j] = original[i - 1].toLowerCase() === modified[j - 1].toLowerCase()
        ? dp[i - 1][j - 1] + 1 // matching token extends the LCS
        : Math.max(dp[i - 1][j], dp[i][j - 1]); // propagate longest match
    }
  }

  return dp;
};

/**
 * Produce diff tokens: same | added | deleted
 *
 * This function reconstructs the actual diff by walking
 * backwards through the LCS matrix.
 *
 * During backtracking:
 * - Diagonal move  → token is unchanged
 * - Left move      → token was added
 * - Up move        → token was deleted
 */
export const diffTokens = (original, modified, dp) => {
  const result = [];

  // Start from the bottom-right of the matrix
  let i = original.length;
  let j = modified.length;

  while (i > 0 && j > 0) {
    /**
     * CASE 1: Tokens match
     * The token is part of the LCS → unchanged
     */
    if (original[i - 1].toLowerCase() === modified[j - 1].toLowerCase()) {
      result.unshift({
        type: 'same',
        value: modified[j - 1], // prefer modified token for rendering
      });
      i -= 1;
      j -= 1;
    } else if (dp[i][j - 1] >= dp[i - 1][j]) {
      /**
       * CASE 2: Token was added
       * Moving left keeps the LCS length the same
       */
      result.unshift({
        type: 'added',
        value: modified[j - 1],
      });
      j -= 1;
    } else {
      /**
       * CASE 3: Token was deleted
       * Moving up preserves the LCS length
       */
      result.unshift({
        type: 'deleted',
        value: original[i - 1],
      });
      i -= 1;
    }
  }

  /**
   * Remaining tokens in the original string were deleted
   */
  while (i > 0) {
    result.unshift({
      type: 'deleted',
      value: original[i - 1],
    });
    i -= 1;
  }

  /**
   * Remaining tokens in the modified string were added
   */
  while (j > 0) {
    result.unshift({
      type: 'added',
      value: modified[j - 1],
    });
    j -= 1;
  }

  return result;
};
